import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  Crown,
  Wallet,
  Sun,
  Users,
  ArrowRight,
  LogOut,
  RefreshCw,
  MoreVertical,
  User,
  Settings as SettingsIcon,
  HelpCircle,
  Download,
  ArrowUpFromLine,
  Activity as ActivityIcon,
  ShieldCheck,
  Rocket,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  TrendingUp,
  Zap,
  Star,
  CircleDollarSign,
} from "lucide-react";

import Layout from "../components/Layout";
import { howItWorks } from "../data/mockData";
import { apiRequest } from "../api";

/* =========================================================
   PREMIUM DESIGN SYSTEM
   FUNCTIONALITY UNCHANGED
========================================================= */

const ACCENTS = {
  blue: {
    bg: "from-[#172B55]/90 via-[#101827] to-[#0A0F1B]",
    border: "border-[#4385FF]/20",
    hoverBorder: "hover:border-[#4385FF]/45",
    icon: "bg-[#4385FF]/10 text-[#79A6FF]",
    glow: "shadow-[0_0_35px_rgba(67,133,255,0.08)]",
    line: "bg-[#4385FF]",
  },
  green: {
    bg: "from-[#0B3D34]/90 via-[#0E1C1C] to-[#0A0F1B]",
    border: "border-[#20D69B]/20",
    hoverBorder: "hover:border-[#20D69B]/45",
    icon: "bg-[#20D69B]/10 text-[#43E7B2]",
    glow: "shadow-[0_0_35px_rgba(32,214,155,0.08)]",
    line: "bg-[#20D69B]",
  },
  gold: {
    bg: "from-[#44330D]/90 via-[#211B10] to-[#0A0F1B]",
    border: "border-[#F5B942]/20",
    hoverBorder: "hover:border-[#F5B942]/45",
    icon: "bg-[#F5B942]/10 text-[#FFD36A]",
    glow: "shadow-[0_0_35px_rgba(245,185,66,0.08)]",
    line: "bg-[#F5B942]",
  },
  purple: {
    bg: "from-[#30205C]/90 via-[#181328] to-[#0A0F1B]",
    border: "border-[#9B6CFF]/20",
    hoverBorder: "hover:border-[#9B6CFF]/45",
    icon: "bg-[#9B6CFF]/10 text-[#BE9BFF]",
    glow: "shadow-[0_0_35px_rgba(155,108,255,0.08)]",
    line: "bg-[#9B6CFF]",
  },
};

/* =========================================================
   STAT CARD
========================================================= */

function StatCardPro(props) {
  const Icon = props.icon;
  const theme = ACCENTS[props.accent];

  return (
    <div
      className={`
        group relative overflow-hidden
        rounded-[22px]
        border ${theme.border} ${theme.hoverBorder}
        bg-gradient-to-br ${theme.bg}
        p-4 sm:p-5
        ${theme.glow}
        transition-all duration-500
        hover:-translate-y-1
        hover:shadow-[0_18px_45px_rgba(0,0,0,0.25)]
      `}
    >
      {/* background glow */}
      <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/[0.025] blur-2xl transition-all duration-500 group-hover:scale-150" />

      <div className="relative flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={`
              flex h-11 w-11 shrink-0 items-center justify-center
              rounded-[14px] border border-white/[0.06]
              ${theme.icon}
              shadow-inner
              transition-all duration-500
              group-hover:scale-110 group-hover:rotate-3
            `}
          >
            <Icon size={19} strokeWidth={2} />
          </div>

          <div className="min-w-0">
            <div className="truncate text-[10px] font-medium uppercase tracking-[0.12em] text-[#71809D]">
              {props.label}
            </div>
            <div className="mt-1 h-px w-7 bg-white/[0.08]" />
          </div>
        </div>

        <div className="hidden h-7 w-7 items-center justify-center rounded-lg bg-white/[0.025] sm:flex">
          <TrendingUp size={12} className="text-[#53617C]" />
        </div>
      </div>

      <div className="relative mt-5 flex items-baseline gap-1.5">
        <span className="truncate text-xl font-bold tracking-tight text-white sm:text-2xl">
          {props.value}
        </span>

        {props.unit ? (
          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-[#71809D]">
            {props.unit}
          </span>
        ) : null}
      </div>

      {props.progress !== undefined ? (
        <div className="relative mt-4">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider text-[#596780]">
              Daily progress
            </span>
            <span className="text-[9px] font-semibold text-[#B9A06A]">
              {Math.min(100, Math.max(0, props.progress))}%
            </span>
          </div>

          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.055]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#F5B942] to-[#FFD978] shadow-[0_0_12px_rgba(245,185,66,0.35)] transition-all duration-700"
              style={{
                width:
                  Math.min(100, Math.max(0, props.progress)) + "%",
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =========================================================
   SKELETON
========================================================= */

function SkeletonCard() {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-white/[0.06] bg-[#0C121F] p-4 sm:p-5">
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite] bg-gradient-to-r from-transparent via-white/[0.035] to-transparent" />

      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-[14px] bg-white/[0.055]" />
        <div className="h-3 w-20 rounded-full bg-white/[0.055]" />
      </div>

      <div className="mt-6 h-7 w-28 rounded-lg bg-white/[0.055]" />
    </div>
  );
}

/* =========================================================
   QUICK ACTION
========================================================= */

function QuickAction(props) {
  const Icon = props.icon;
  const theme = ACCENTS[props.accent];

  return (
    <Link
      to={props.to}
      className={`
        group relative overflow-hidden
        flex min-w-0 flex-col items-center gap-2.5
        rounded-[20px]
        border ${theme.border} ${theme.hoverBorder}
        bg-[#0D1421]
        px-2 py-4
        text-center
        transition-all duration-400
        hover:-translate-y-1
        hover:bg-[#111A2B]
        active:scale-[0.96]
      `}
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent opacity-0 transition group-hover:opacity-100" />

      <div
        className={`
          flex h-11 w-11 items-center justify-center
          rounded-[14px]
          border border-white/[0.05]
          ${theme.icon}
          transition-all duration-500
          group-hover:scale-110 group-hover:-rotate-3
        `}
      >
        <Icon size={19} strokeWidth={2} />
      </div>

      <div className="min-w-0">
        <div className="truncate text-[11px] font-semibold text-white">
          {props.title}
        </div>
        <div className="mt-0.5 truncate text-[9px] text-[#65728B]">
          {props.subtitle}
        </div>
      </div>
    </Link>
  );
}

/* =========================================================
   DASHBOARD
========================================================= */

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [vipLevels, setVipLevels] = useState([]);
  const [activeVips, setActiveVips] = useState([]);
  const [usdtLog, setUsdtLog] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const navigate = useNavigate();

  useEffect(function () {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return function cleanup() {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const current = vipLevels.find(function (level) {
    return Number(level.id) === Number(user && user.vip_level);
  });

  const loadData = useCallback(function (showLoader) {
    return (async function () {
      try {
        if (showLoader) {
          setRefreshing(true);
        }

        setError("");

        const results = await Promise.all([
          apiRequest("/users/me"),
          apiRequest("/vip-levels"),
          apiRequest("/users/me/points-log"),
        ]);

        const profile = results[0];
        const levels = results[1];
        const log = results[2];

        setUser(profile);
        setVipLevels(Array.isArray(levels) ? levels : []);
        setUsdtLog(Array.isArray(log) ? log : []);

        try {
          const active = await apiRequest("/vip-levels/my-active");
          setActiveVips(Array.isArray(active) ? active : []);
        } catch (activeError) {
          console.warn("Active VIP endpoint unavailable:", activeError);

          const fallbackVip = (Array.isArray(levels) ? levels : [])
            .filter(function (level) {
              return (
                Number(level.id) === Number(profile && profile.vip_level)
              );
            })
            .map(function (level) {
              return {
                id: Number(level.id),
                name: level.name || "VIP " + String(level.id),
                deposit: Number(
                  level.usdt_required !== undefined
                    ? level.usdt_required
                    : level.points_required !== undefined
                    ? level.points_required
                    : level.deposit !== undefined
                    ? level.deposit
                    : 0
                ),
                daily: Number(
                  level.daily_usdt !== undefined
                    ? level.daily_usdt
                    : level.daily_points !== undefined
                    ? level.daily_points
                    : level.daily_earnings !== undefined
                    ? level.daily_earnings
                    : level.daily !== undefined
                    ? level.daily
                    : 0
                ),
                color: level.color || "#5B8DEF",
                status: "active",
              };
            });

          setActiveVips(fallbackVip);
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
        setError(
          (err && err.message) || "Failed to load dashboard."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    })();
  }, []);

  useEffect(
    function () {
      loadData(false);
    },
    [loadData]
  );

  useEffect(
    function () {
      const interval = setInterval(function () {
        loadData(false);
      }, 10000);

      return function cleanup() {
        clearInterval(interval);
      };
    },
    [loadData]
  );

  const handleSelectPlan = async function (id) {
    const selectedLevel = vipLevels.find(function (level) {
      return Number(level.id) === Number(id);
    });

    if (!selectedLevel) {
      alert("VIP level not found.");
      return;
    }

    const required = Number(
      selectedLevel.usdt_required !== undefined
        ? selectedLevel.usdt_required
        : selectedLevel.points_required !== undefined
        ? selectedLevel.points_required
        : selectedLevel.deposit !== undefined
        ? selectedLevel.deposit
        : 0
    );

    const balance = Number((user && user.usdt_balance) || 0);

    if (!Number.isFinite(required) || required <= 0) {
      alert("Invalid VIP deposit amount.");
      return;
    }

    if (!Number.isFinite(balance)) {
      alert("Invalid USDT balance.");
      return;
    }

    const vipName =
      selectedLevel.name || "VIP " + String(selectedLevel.id);

    const currentVip = current;
    const isUpgrade =
      !!currentVip && Number(currentVip.id) !== Number(id);

    let confirmMessage;
    let neededAmount;

    if (isUpgrade) {
      const currentRequired = Number(
        currentVip.usdt_required !== undefined
          ? currentVip.usdt_required
          : currentVip.points_required !== undefined
          ? currentVip.points_required
          : currentVip.deposit !== undefined
          ? currentVip.deposit
          : 0
      );

      const difference = required - currentRequired;

      neededAmount = difference;

      confirmMessage =
        "Upgrade VIP Plan\n\n" +
        "Current VIP: " +
        (currentVip.name || "VIP " + currentVip.id) +
        "\n" +
        "New VIP: " +
        vipName +
        "\n\n" +
        "Current requirement: " +
        currentRequired.toFixed(2) +
        " USDT\n" +
        "New requirement: " +
        required.toFixed(2) +
        " USDT\n" +
        "Upgrade difference: " +
        difference.toFixed(2) +
        " USDT\n\n" +
        "Available balance: " +
        balance.toFixed(2) +
        " USDT\n\n" +
        "Only the upgrade difference will be deducted. Continue?";
    } else {
      neededAmount = required;

      confirmMessage =
        "Are you sure you want to activate " +
        vipName +
        "?\n\nRequired: " +
        required.toFixed(2) +
        " USDT\nAvailable: " +
        balance.toFixed(2) +
        " USDT";
    }

    if (balance < neededAmount) {
      alert(
        "Insufficient USDT.\n\nRequired: " +
          neededAmount.toFixed(2) +
          " USDT\nAvailable: " +
          balance.toFixed(2) +
          " USDT."
      );

      return;
    }

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) {
      return;
    }

    try {
      const response = await apiRequest(
        "/vip-levels/" + id + "/unlock",
        {
          method: "POST",
        }
      );

      alert(
        (response && response.message) ||
          vipName + " activated successfully."
      );

      await loadData(true);
    } catch (err) {
      console.error("VIP activation error:", err);

      alert(
        (err && err.message) ||
          "Unable to activate VIP level."
      );
    }
  };

  const handleClaimDaily = async function () {
    if (activeVips.length === 0 && !current) {
      alert("Activate a VIP level first.");
      return;
    }

    try {
      const response = await apiRequest(
        "/vip-levels/claim-daily",
        {
          method: "POST",
        }
      );

      alert(
        (response && response.message) ||
          "Daily earnings claimed successfully."
      );

      await loadData(true);
    } catch (err) {
      console.error("Daily claim error:", err);

      alert(
        (err && err.message) ||
          "Unable to claim daily earnings."
      );
    }
  };

  const handleLogout = function () {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const activeVipLevels =
    activeVips.length > 0
      ? activeVips
      : current
      ? [current]
      : [];

  const dailyEarnings = current
    ? Number(
        current.daily_usdt !== undefined
          ? current.daily_usdt
          : current.daily_points !== undefined
          ? current.daily_points
          : current.daily_earnings !== undefined
          ? current.daily_earnings
          : current.daily !== undefined
          ? current.daily
          : 0
      )
    : activeVips.reduce(function (total, vip) {
        return (
          total +
          Number(
            vip.daily !== undefined
              ? vip.daily
              : vip.daily_usdt !== undefined
              ? vip.daily_usdt
              : 0
          )
        );
      }, 0);

  const firstName = ((user && user.name) || "User").split(" ")[0];

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="relative overflow-hidden rounded-[26px] border border-white/[0.06] bg-gradient-to-br from-[#101A2C] to-[#0A0F19] p-5 sm:p-6">
          <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-[#4385FF]/10 blur-3xl" />

          <div className="relative mb-6 space-y-2">
            <div className="h-3 w-24 animate-pulse rounded-full bg-white/[0.06]" />
            <div className="h-8 w-44 animate-pulse rounded-lg bg-white/[0.06]" />
            <div className="h-3 w-60 animate-pulse rounded-full bg-white/[0.04]" />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>

          <div className="mt-6 h-48 animate-pulse rounded-[22px] border border-white/[0.06] bg-[#0C121F]" />
        </div>
      </Layout>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error && !user) {
    return (
      <Layout title="Dashboard">
        <div className="flex min-h-[60vh] flex-col items-center justify-center px-5 text-center">
          <div className="relative mb-5">
            <div className="absolute inset-0 rounded-[22px] bg-[#E5566B]/20 blur-xl" />

            <div className="relative flex h-16 w-16 items-center justify-center rounded-[22px] border border-[#E5566B]/20 bg-[#E5566B]/10">
              <AlertCircle size={27} className="text-[#F07889]" />
            </div>
          </div>

          <div className="text-base font-semibold text-white">
            Couldn't load dashboard
          </div>

          <div className="mt-1.5 max-w-[280px] text-xs leading-relaxed text-[#74819A]">
            {error}
          </div>

          <button
            onClick={function () {
              setLoading(true);
              loadData(true);
            }}
            className="mt-6 flex items-center gap-2 rounded-xl border border-[#4385FF]/20 bg-[#4385FF]/10 px-5 py-2.5 text-xs font-semibold text-[#82ABFF] transition hover:border-[#4385FF]/40 hover:bg-[#4385FF]/15 active:scale-95"
          >
            <RefreshCw size={14} />
            Try Again
          </button>
        </div>
      </Layout>
    );
  }

  /* =========================================================
     MAIN UI
  ========================================================= */

  return (
    <Layout title="Dashboard">
      {/* MOBILE MENU */}
      <div className="mb-4 flex items-center justify-end lg:hidden">
        <div className="relative" ref={menuRef}>
          <button
            onClick={function () {
              setMenuOpen(function (v) {
                return !v;
              });
            }}
            aria-label="Open menu"
            className="group flex h-10 w-10 items-center justify-center rounded-xl border border-white/[0.07] bg-[#0C131F] text-[#8290A9] shadow-lg transition hover:border-white/[0.12] hover:bg-[#121B2B] hover:text-white active:scale-95"
          >
            <MoreVertical
              size={17}
              className="transition group-hover:scale-110"
            />
          </button>

          {menuOpen ? (
            <div className="absolute right-0 top-12 z-50 w-56 overflow-hidden rounded-[20px] border border-white/[0.08] bg-[#101725]/95 p-1.5 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-2xl">
              <Link
                to="/settings"
                onClick={function () {
                  setMenuOpen(false);
                }}
                className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium text-[#D8DEEA] transition hover:bg-white/[0.05] hover:text-white"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#4385FF]/10 text-[#79A6FF]">
                  <User size={15} />
                </span>
                Profile
              </Link>

              <Link
                to="/settings"
                onClick={function () {
                  setMenuOpen(false);
                }}
                className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium text-[#D8DEEA] transition hover:bg-white/[0.05] hover:text-white"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#9B6CFF]/10 text-[#BE9BFF]">
                  <SettingsIcon size={15} />
                </span>
                Settings
              </Link>

              <Link
                to="/support"
                onClick={function () {
                  setMenuOpen(false);
                }}
                className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium text-[#D8DEEA] transition hover:bg-white/[0.05] hover:text-white"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#20D69B]/10 text-[#43E7B2]">
                  <HelpCircle size={15} />
                </span>
                Help & Support
              </Link>

              <div className="my-1.5 h-px bg-white/[0.06]" />

              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-medium text-[#F07889] transition hover:bg-[#E5566B]/10"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E5566B]/10">
                  <LogOut size={15} />
                </span>
                Log out
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* WELCOME HERO */}
      <div className="relative mb-6 overflow-hidden rounded-[26px] border border-white/[0.07] bg-gradient-to-br from-[#111E35] via-[#0D1524] to-[#0A0F18] p-5 sm:p-6">
        <div className="pointer-events-none absolute -right-20 -top-28 h-64 w-64 rounded-full bg-[#4385FF]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-[#9B6CFF]/[0.07] blur-3xl" />

        <div className="relative">
          <div className="mb-2 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#20D69B] shadow-[0_0_10px_rgba(32,214,155,0.8)]" />

            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#70809C]">
              Your Dashboard
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Welcome back, {firstName}
            </h1>

            <span className="text-2xl">👋</span>
          </div>

          <p className="mt-2 max-w-xl text-xs leading-relaxed text-[#71809B] sm:text-sm">
            Track your earnings, manage your VIP level and grow with VIP Earn.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-full border border-white/[0.07] bg-white/[0.035] px-3 py-1.5">
              <Sparkles size={12} className="text-[#F5B942]" />
              <span className="text-[10px] font-medium text-[#A5B0C5]">
                Premium Account
              </span>
            </div>

            <div className="flex items-center gap-2 rounded-full border border-[#20D69B]/10 bg-[#20D69B]/[0.04] px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#20D69B]" />
              <span className="text-[10px] font-medium text-[#62D9B0]">
                System Online
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* VIP BRAND BANNER */}
      <div className="group relative mb-5 overflow-hidden rounded-[24px] border border-[#F5B942]/15 bg-gradient-to-br from-[#251D0D] via-[#15151A] to-[#0B101A] p-4 sm:p-5">
        <div className="absolute -right-16 -top-20 h-44 w-44 rounded-full bg-[#F5B942]/10 blur-3xl transition-transform duration-700 group-hover:scale-125" />
        <div className="absolute -bottom-20 left-1/2 h-36 w-36 rounded-full bg-[#9B6CFF]/10 blur-3xl" />

        <div className="relative flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-[16px] bg-[#F5B942]/20 blur-md" />

              <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] border border-[#F5B942]/20 bg-gradient-to-br from-[#F5B942] to-[#9B6CFF] shadow-[0_8px_25px_rgba(245,185,66,0.16)]">
                <Crown
                  size={20}
                  strokeWidth={2.2}
                  className="text-[#0A0E16]"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-sm font-bold tracking-wide text-white">
                  VIP EARN
                </div>

                <span className="rounded-full bg-[#F5B942]/10 px-2 py-0.5 text-[8px] font-bold tracking-wider text-[#F5C85D]">
                  PREMIUM
                </span>
              </div>

              <div className="mt-1 text-[10px] text-[#8B8A87]">
                VIP Rewards • Earn More • Grow Faster
              </div>
            </div>
          </div>

          <Sparkles
            size={19}
            className="shrink-0 text-[#F5B942] opacity-60 transition duration-500 group-hover:rotate-12 group-hover:scale-110"
          />
        </div>
      </div>

      {/* ERROR */}
      {error ? (
        <div className="mb-5 flex items-center gap-3 rounded-[16px] border border-[#E5566B]/20 bg-[#E5566B]/[0.06] px-4 py-3 text-xs text-[#F07889]">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E5566B]/10">
            <AlertCircle size={15} />
          </div>

          <span>{error}</span>
        </div>
      ) : null}

      {/* STATS */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <StatCardPro
          icon={Wallet}
          label="USDT Balance"
          value={Number((user && user.usdt_balance) || 0).toFixed(2)}
          unit="USDT"
          accent="blue"
        />

        <StatCardPro
          icon={Crown}
          label="Current VIP"
          value={
            current
              ? current.name
              : activeVips.length > 0
              ? (activeVips[0] && activeVips[0].name) || "Active"
              : "None"
          }
          unit=""
          accent="green"
        />

        <StatCardPro
          icon={Sun}
          label="Daily Earnings"
          value={Number(dailyEarnings || 0).toFixed(2)}
          unit="USDT"
          accent="gold"
          progress={dailyEarnings > 0 ? 100 : 0}
        />

        <StatCardPro
          icon={Users}
          label="Referrals"
          value={Number((user && user.referral_count) || 0)}
          unit=""
          accent="purple"
        />
      </div>

      {/* REFRESH STATUS */}
      <div className="my-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#20D69B] opacity-50" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#20D69B]" />
          </span>

          <span className="text-[10px] text-[#6E7B95]">
            Last updated: just now
          </span>
        </div>

        <button
          onClick={function () {
            loadData(true);
          }}
          disabled={refreshing}
          aria-label="Refresh dashboard"
          className="group flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.025] px-2.5 py-1.5 text-[10px] font-medium text-[#7798D7] transition hover:border-[#4385FF]/20 hover:bg-[#4385FF]/10 hover:text-[#91B4FF] disabled:opacity-50"
        >
          <RefreshCw
            size={12}
            className={
              refreshing
                ? "animate-spin"
                : "transition-transform duration-500 group-hover:rotate-180"
            }
          />
          Refresh
        </button>
      </div>

      {/* VIP LEVELS */}
      <div
        id="vip-levels"
        className="relative overflow-hidden rounded-[24px] border border-white/[0.07] bg-[#0B111D] p-4 sm:p-6"
      >
        <div className="absolute -right-24 -top-24 h-56 w-56 rounded-full bg-[#9B6CFF]/[0.045] blur-3xl" />

        <div className="relative">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F5B942]/10 text-[#F5C95F]">
                  <Crown size={17} />
                </div>

                <div>
                  <h2 className="text-[15px] font-bold text-white">
                    VIP Levels
                  </h2>

                  <div className="mt-0.5 text-[9px] uppercase tracking-[0.15em] text-[#596780]">
                    Choose your earning tier
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/vip"
              className="group flex items-center gap-1 rounded-lg border border-white/[0.05] bg-white/[0.02] px-2.5 py-1.5 text-[10px] font-semibold text-[#7099EA] transition hover:border-[#4385FF]/20 hover:bg-[#4385FF]/10"
            >
              View All
              <ChevronRight
                size={12}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>

          <p className="mb-5 mt-3 max-w-xl text-[11px] leading-relaxed text-[#697792]">
            Choose a VIP level to activate and start earning daily.
          </p>

          {vipLevels.length === 0 ? (
            <div className="rounded-[18px] border border-white/[0.06] bg-[#0E1522] p-8 text-center">
              <CircleDollarSign
                size={28}
                className="mx-auto mb-3 text-[#4D5B75]"
              />

              <div className="text-xs text-[#71809A]">
                No VIP levels available.
              </div>
            </div>
          ) : (
            <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-3 scrollbar-thin">
              {vipLevels.map(function (lvl) {
                const isActive =
                  Number(user && user.vip_level) === Number(lvl.id) ||
                  activeVips.some(function (vip) {
                    return (
                      Number(
                        vip.id !== undefined
                          ? vip.id
                          : vip.vip_level_id
                      ) === Number(lvl.id)
                    );
                  });

                const deposit = Number(
                  lvl.usdt_required !== undefined
                    ? lvl.usdt_required
                    : lvl.points_required !== undefined
                    ? lvl.points_required
                    : lvl.deposit !== undefined
                    ? lvl.deposit
                    : 0
                );

                const daily = Number(
                  lvl.daily_usdt !== undefined
                    ? lvl.daily_usdt
                    : lvl.daily_points !== undefined
                    ? lvl.daily_points
                    : lvl.daily_earnings !== undefined
                    ? lvl.daily_earnings
                    : lvl.daily !== undefined
                    ? lvl.daily
                    : 0
                );

                const color = lvl.color || "#5B8DEF";
                const name = lvl.name || "VIP " + String(lvl.id);

                const isLocked =
                  current &&
                  Number(deposit) <
                    Number(
                      current.usdt_required !== undefined
                        ? current.usdt_required
                        : current.deposit !== undefined
                        ? current.deposit
                        : 0
                    ) &&
                  !isActive;

                return (
                  <div
                    key={lvl.id}
                    className={`
                      group relative shrink-0 w-[165px] snap-start
                      overflow-hidden rounded-[21px]
                      border p-4
                      transition-all duration-500
                      ${
                        isActive
                          ? "border-[#9B6CFF]/45 bg-gradient-to-br from-[#241A43] to-[#0D1420] shadow-[0_15px_40px_rgba(139,92,246,0.12)]"
                          : isLocked
                          ? "border-white/[0.04] bg-[#090E17] opacity-45"
                          : "border-white/[0.06] bg-[#0F1725] hover:-translate-y-1 hover:border-white/[0.12] hover:bg-[#111B2B]"
                      }
                    `}
                  >
                    <div
                      className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full opacity-20 blur-2xl transition group-hover:scale-150"
                      style={{ backgroundColor: color }}
                    />

                    <div className="relative flex items-center justify-between">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-[13px] border border-white/[0.05]"
                        style={{
                          backgroundColor: color + "18",
                          color: color,
                        }}
                      >
                        <Crown size={18} />
                      </div>

                      {isActive ? (
                        <span className="flex items-center gap-1 rounded-full border border-[#20D69B]/15 bg-[#20D69B]/10 px-2 py-1 text-[8px] font-bold tracking-wide text-[#47DDB1]">
                          <CheckCircle2 size={9} />
                          ACTIVE
                        </span>
                      ) : (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/[0.025] text-[#4F5D76]">
                          <Star size={11} />
                        </span>
                      )}
                    </div>

                    <div className="relative mt-4">
                      <div className="truncate text-sm font-bold text-white">
                        {name}
                      </div>

                      <div className="mt-1 h-px w-8 bg-white/[0.08]" />
                    </div>

                    <div className="relative mt-4">
                      <div className="text-[8px] font-medium uppercase tracking-[0.14em] text-[#58657D]">
                        Deposit
                      </div>

                      <div className="mt-1 text-sm font-bold text-[#E7EBF3]">
                        {deposit.toFixed(2)}
                        <span className="ml-1 text-[9px] font-medium text-[#68758D]">
                          USDT
                        </span>
                      </div>
                    </div>

                    <div className="relative mt-3">
                      <div className="text-[8px] font-medium uppercase tracking-[0.14em] text-[#58657D]">
                        Daily Reward
                      </div>

                      <div
                        className="mt-1 text-sm font-bold"
                        style={{ color: color }}
                      >
                        {daily.toFixed(2)}
                        <span className="ml-1 text-[9px] font-medium opacity-60">
                          USDT
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={function () {
                        handleSelectPlan(lvl.id);
                      }}
                      disabled={isActive || isLocked}
                      className={`
                        relative mt-4 w-full min-h-[40px]
                        rounded-xl
                        text-[10px] font-bold
                        transition-all duration-300
                        ${
                          isActive
                            ? "cursor-default border border-[#20D69B]/15 bg-[#20D69B]/10 text-[#47DDB1]"
                            : isLocked
                            ? "cursor-not-allowed bg-white/[0.04] text-[#4F5A70]"
                            : "text-white shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:scale-[0.97]"
                        }
                      `}
                      style={
                        isActive || isLocked
                          ? undefined
                          : {
                              backgroundColor: color,
                              boxShadow:
                                "0 8px 20px " + color + "25",
                            }
                      }
                    >
                      {isActive
                        ? "Active"
                        : isLocked
                        ? "Locked"
                        : current
                        ? "Upgrade"
                        : "Select Plan"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ACTIVE VIPS */}
          {activeVipLevels.length > 0 ? (
            <div className="mt-5 border-t border-white/[0.06] pt-5">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#596780]">
                  Active VIP Levels
                </div>

                <span className="rounded-full bg-[#20D69B]/10 px-2 py-1 text-[8px] font-semibold text-[#4ADFB4]">
                  {activeVipLevels.length} ACTIVE
                </span>
              </div>

              <div className="space-y-2">
                {activeVipLevels.map(function (level, index) {
                  return (
                    <div
                      key={level.user_vip_id || level.id || index}
                      className="group flex items-center justify-between rounded-[15px] border border-white/[0.045] bg-[#101827] px-3.5 py-3 transition hover:border-[#20D69B]/15 hover:bg-[#121C2D]"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#20D69B]/10">
                          <div className="h-1.5 w-1.5 rounded-full bg-[#20D69B] shadow-[0_0_9px_rgba(32,214,155,0.8)]" />
                        </div>

                        <span className="truncate text-xs font-medium text-white">
                          {level.name ||
                            "VIP " + String(level.id)}
                        </span>
                      </div>

                      <span className="flex shrink-0 items-center gap-1.5 text-[10px] font-semibold text-[#3FD9AD]">
                        <CheckCircle2 size={12} />
                        Active
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="mt-6 grid grid-cols-4 gap-2.5 sm:gap-3">
        <QuickAction
          icon={Download}
          title="Deposit"
          subtitle="Add USDT"
          to="/deposit"
          accent="blue"
        />

        <QuickAction
          icon={ArrowUpFromLine}
          title="Withdraw"
          subtitle="Withdraw"
          to="/withdraw"
          accent="green"
        />

        <QuickAction
          icon={ActivityIcon}
          title="Activity"
          subtitle="Transactions"
          to="/transactions"
          accent="purple"
        />

        <QuickAction
          icon={Users}
          title="Referrals"
          subtitle="Invite & Earn"
          to="/referrals"
          accent="gold"
        />
      </div>

      {/* LOWER CONTENT */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* RECENT ACTIVITY */}
        <div className="overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#0B111D] p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#4385FF]/10 text-[#79A6FF]">
                  <ActivityIcon size={15} />
                </div>

                <div>
                  <div className="text-sm font-bold text-white">
                    Recent Activity
                  </div>

                  <div className="text-[9px] text-[#56637B]">
                    Latest account activity
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/transactions"
              className="group flex items-center gap-1 rounded-lg px-2 py-1.5 text-[10px] font-semibold text-[#7199E8] transition hover:bg-[#4385FF]/10"
            >
              View All
              <ChevronRight
                size={12}
                className="transition group-hover:translate-x-0.5"
              />
            </Link>
          </div>

          {usdtLog.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-[17px] border border-dashed border-white/[0.07] bg-white/[0.015] py-10 text-center">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.035] text-[#536078]">
                <ActivityIcon size={17} />
              </div>

              <p className="text-xs text-[#66738C]">
                No activity yet.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {usdtLog.slice(0, 6).map(function (log) {
                const amount = Number(
                  log.amount_usdt !== undefined
                    ? log.amount_usdt
                    : log.amount !== undefined
                    ? log.amount
                    : 0
                );

                const positive = amount >= 0;

                return (
                  <div
                    key={log.id}
                    className="group flex items-center gap-3 rounded-[14px] border-b border-white/[0.04] px-2 py-3 transition last:border-0 hover:bg-white/[0.025]"
                  >
                    <div
                      className={`
                        flex h-9 w-9 shrink-0 items-center justify-center
                        rounded-xl border
                        ${
                          positive
                            ? "border-[#20D69B]/10 bg-[#20D69B]/10 text-[#43DCAF]"
                            : "border-[#E5566B]/10 bg-[#E5566B]/10 text-[#F07889]"
                        }
                      `}
                    >
                      {positive ? (
                        <CheckCircle2 size={15} />
                      ) : (
                        <ArrowUpFromLine size={15} />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold capitalize text-white">
                        {String(
                          log.type || "Activity"
                        ).replace(/_/g, " ")}
                      </div>

                      <div className="mt-0.5 text-[9px] text-[#606D85]">
                        {log.created_at
                          ? new Date(
                              log.created_at
                            ).toLocaleString()
                          : ""}
                      </div>
                    </div>

                    <div
                      className={`
                        whitespace-nowrap text-xs font-bold
                        ${
                          positive
                            ? "text-[#3FD9AD]"
                            : "text-[#F07889]"
                        }
                      `}
                    >
                      {amount > 0 ? "+" : ""}
                      {amount.toFixed(2)} USDT
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SIDE CARDS */}
        <div className="space-y-4">
          {/* CLAIM */}
          <div className="relative overflow-hidden rounded-[22px] border border-[#20D69B]/10 bg-gradient-to-br from-[#102820] to-[#0B121C] p-5">
            <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#20D69B]/10 blur-2xl" />

            <div className="relative">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#20D69B]/10 text-[#43DCAF]">
                  <Sun size={15} />
                </div>

                <div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-[#61708A]">
                    Daily Earnings
                  </div>

                  <div className="mt-0.5 text-xs font-semibold text-white">
                    Claim your reward
                  </div>
                </div>
              </div>

              <button
                onClick={handleClaimDaily}
                disabled={activeVipLevels.length === 0}
                className="group flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#20D69B] text-xs font-bold text-[#07120F] shadow-[0_8px_25px_rgba(32,214,155,0.12)] transition hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35"
              >
                <Zap
                  size={14}
                  className="transition group-hover:scale-110"
                />

                {activeVipLevels.length > 0
                  ? "Claim Daily Earnings"
                  : "Activate VIP First"}
              </button>
            </div>
          </div>

          {/* UPGRADE */}
          <div
            onClick={function () {
              const el = document.getElementById("vip-levels");

              if (el) {
                el.scrollIntoView({
                  behavior: "smooth",
                });
              }
            }}
            className="group relative cursor-pointer overflow-hidden rounded-[22px] border border-[#9B6CFF]/15 bg-gradient-to-br from-[#21153E] via-[#151225] to-[#0B101A] p-5 transition-all duration-500 hover:-translate-y-1 hover:border-[#9B6CFF]/30"
          >
            <div className="absolute -right-14 -top-14 h-36 w-36 rounded-full bg-[#9B6CFF]/10 blur-3xl transition group-hover:scale-125" />

            <div className="relative">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-[#9B6CFF]/15 bg-[#9B6CFF]/10 text-[#BE9BFF]">
                <Rocket
                  size={19}
                  className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-6"
                />
              </div>

              <div className="text-base font-bold leading-tight text-white">
                Earn More With VIP Earn
              </div>

              <div className="mt-1 text-[10px] text-[#9181B9]">
                Higher VIP, Higher Earnings
              </div>

              <div className="mt-4 flex items-center gap-1.5 text-[10px] font-bold text-[#BE9BFF]">
                Upgrade Now
                <ChevronRight
                  size={13}
                  className="transition group-hover:translate-x-1"
                />
              </div>
            </div>
          </div>

          {/* ABOUT */}
          <div className="rounded-[22px] border border-white/[0.06] bg-[#0B111D] p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#4385FF]/10 bg-[#4385FF]/10 text-[#79A6FF]">
                <ShieldCheck size={18} />
              </div>

              <div>
                <div className="mb-1 text-sm font-bold text-white">
                  About VIP Earn
                </div>

                <div className="text-[10px] leading-[1.7] text-[#66738C]">
                  VIP Earn is a modern rewards platform designed to provide
                  users with a simple way to manage their VIP account and
                  track daily USDT earnings.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="relative mt-6 overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#0B111D] p-5 sm:p-6">
        <div className="absolute -left-20 -bottom-20 h-44 w-44 rounded-full bg-[#4385FF]/[0.035] blur-3xl" />

        <div className="relative">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#4385FF]/10 text-[#79A6FF]">
              <Sparkles size={16} />
            </div>

            <div>
              <h2 className="text-[15px] font-bold text-white">
                How It Works
              </h2>

              <div className="mt-0.5 text-[9px] uppercase tracking-[0.15em] text-[#596780]">
                Simple steps to start earning
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {Array.isArray(howItWorks)
              ? howItWorks.map(function (step, i) {
                  return (
                    <div
                      key={i}
                      className="group relative rounded-[18px] border border-white/[0.045] bg-[#0F1724] p-4 transition-all duration-400 hover:-translate-y-1 hover:border-[#4385FF]/15 hover:bg-[#111B2B]"
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#161F30] text-[#79A6FF] transition-all duration-500 group-hover:bg-[#4385FF]/10 group-hover:scale-105">
                          {step.icon ? (
                            <step.icon size={17} />
                          ) : null}

                          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#4385FF] text-[7px] font-bold text-white">
                            {i + 1}
                          </span>
                        </div>

                        <div className="min-w-0">
                          <div className="mb-1 text-xs font-semibold text-white">
                            {step.title}
                          </div>

                          <div className="text-[10px] leading-relaxed text-[#66738C]">
                            {step.desc}
                          </div>
                        </div>
                      </div>

                      {i < howItWorks.length - 1 ? (
                        <ArrowRight
                          size={14}
                          className="absolute -right-4 top-7 z-10 hidden text-[#34415B] lg:block"
                        />
                      ) : null}
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="pb-24 pt-7 text-center lg:pb-6">
        <div className="mx-auto flex w-fit items-center gap-2 rounded-full border border-white/[0.045] bg-white/[0.02] px-3 py-1.5">
          <Crown size={10} className="text-[#F5B942]" />
          <span className="text-[9px] font-medium uppercase tracking-[0.18em] text-[#4F5C73]">
            VIP Earn
          </span>
          <span className="h-1 w-1 rounded-full bg-[#29344A]" />
          <span className="text-[8px] text-[#46526A]">
            Premium Rewards
          </span>
        </div>
      </footer>

      {/* COMPONENT-LOCAL ANIMATION */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }

        .scrollbar-thin::-webkit-scrollbar {
          height: 4px;
        }

        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }

        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.08);
          border-radius: 999px;
        }

        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.15);
        }
      `}</style>
    </Layout>
  );
}